import { motion, AnimatePresence } from "framer-motion";
import {
  X, Play, Heart, Star, Sparkle, Flower as FlowerIcon,
  NumberSquareOne, NumberCircleThree, Plus, Minus, Scales, Shapes, StackSimple, XSquare, Divide,
  Coins, Clock, Ruler, ChartPieSlice, GridFour, SquaresFour, Triangle, ChartBar, Percent, Cube,
  Function, ChartLineUp, ArrowsLeftRight, Exam, Waveform, TextAa, BookOpen, Ear, ChatCircleDots,
  BookBookmark, Books, PencilLine, MagnifyingGlass, Quotes, PenNib, Detective, Basket, Drop,
  Trophy, Palette, GearSix, UserCircle,
} from "@phosphor-icons/react";
import { ChildAvatar } from "../select-child/ChildAvatar";
import { STAGES } from "./yearConfig";

const TOPIC_ICONS = {
  Counting: NumberSquareOne, Numbers: NumberCircleThree, Addition: Plus, Subtraction: Minus,
  "Comparing Numbers": Scales, Shapes: Shapes, "Place Value": StackSimple, Multiplication: XSquare,
  Division: Divide, Money: Coins, Time: Clock, Measurement: Ruler, Fractions: ChartPieSlice,
  "Multiplication Tables": GridFour, Decimals: NumberCircleThree, Area: SquaresFour, Geometry: Triangle,
  Statistics: ChartBar, Percentages: Percent, Volume: Cube, Algebra: Function, Graphs: ChartLineUp,
  Ratio: ArrowsLeftRight, "SATs Practice": Exam,
  "Letter Sounds": Waveform, "Reading Words": TextAa, "Story Time": BookOpen, Listening: Ear,
  Speaking: ChatCircleDots, Vocabulary: BookBookmark, Reading: BookOpen, Grammar: TextAa,
  Stories: Books, Writing: PencilLine, Comprehension: MagnifyingGlass, Spelling: TextAa,
  Poetry: Quotes, "Reading Comprehension": MagnifyingGlass, "Creative Writing": PenNib, Inference: Detective,
};

const ADV_ICONS = { Drop, Plus, Basket, Clock, Books, ChartPieSlice, GridFour, MagnifyingGlass, Ruler, Coins, Quotes, Percent, Cube, PenNib, Function, Exam, Detective };

const FUTURE_SECTIONS = [
  { id: "achievements", label: "Achievements", icon: Trophy },
  { id: "favourites", label: "Favourites", icon: Heart },
  { id: "creations", label: "My Creations", icon: Palette },
  { id: "settings", label: "Settings", icon: GearSix },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Sheet({ open, onClose, title, children, testid }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="absolute inset-0 z-40 flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <button aria-label="Close" data-testid="sheet-backdrop" onClick={onClose} className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
          <motion.div
            data-testid={testid}
            role="dialog"
            aria-label={title}
            initial={{ y: 80, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="relative flex max-h-[86svh] w-full max-w-md flex-col rounded-t-[2rem] bg-card shadow-soft-lg sm:mx-4 sm:rounded-[2rem]"
          >
            <div className="flex items-center justify-between px-5 pt-5">
              <h2 className="font-display text-xl font-black text-foreground">{title}</h2>
              <button onClick={onClose} data-testid="sheet-close" aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-border">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="scroll-soft min-h-0 flex-1 overflow-y-auto px-5 pb-7 pt-3 sm:pb-5">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SubjectSheet({ open, onClose, subject, year, cfg, onPlay, onTopicTap }) {
  const topics = cfg[subject];
  const experiences = cfg.experiences[subject];
  const isMaths = subject === "maths";
  return (
    <Sheet open={open} onClose={onClose} title={isMaths ? "The Counting Tree" : "The Story Book"} testid={`panel-${subject}`}>
      <p className="mb-3 text-sm font-semibold text-muted-foreground">
        Year {year} {isMaths ? "Maths" : "English"} · what {/^[AEIOU]/.test(STAGES[year]) ? "an" : "a"} {STAGES[year]} learns here
      </p>
      <div className="grid grid-cols-2 gap-2.5">
        {topics.map((t) => {
          const exp = experiences.find((e) => e.topic === t);
          const Icon = TOPIC_ICONS[t] || Sparkle;
          return exp ? (
            <button
              key={t}
              data-testid={`topic-${slug(t)}`}
              onClick={() => onPlay(exp)}
              className="group flex min-h-[64px] items-center gap-2.5 rounded-2xl border-2 border-primary/50 bg-accent px-3 text-left transition-all hover:border-primary hover:shadow-soft active:scale-[0.97]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-glow">
                <Icon size={18} weight="bold" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm font-extrabold text-foreground">{t}</span>
                <span className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide text-primary-strong">
                  <Play size={9} weight="fill" /> {exp.label}
                </span>
              </span>
            </button>
          ) : (
            <button
              key={t}
              data-testid={`topic-${slug(t)}`}
              onClick={() => onTopicTap(t)}
              className="flex min-h-[64px] items-center gap-2.5 rounded-2xl border-2 border-border bg-background px-3 text-left transition-all hover:border-teal/60 active:scale-[0.97]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                <Icon size={18} weight="bold" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm font-extrabold text-foreground">{t}</span>
                <span className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide text-teal-ink">
                  <Sparkle size={9} weight="fill" /> Growing soon
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

export function WorldPanels({ panel, onClose, child, year, cfg, elementsCount, adventure, onPlay, onTopicTap }) {
  const AdvIcon = (adventure && ADV_ICONS[adventure.icon]) || Sparkle;
  return (
    <>
      <SubjectSheet open={panel === "maths"} onClose={onClose} subject="maths" year={year} cfg={cfg} onPlay={onPlay} onTopicTap={onTopicTap} />
      <SubjectSheet open={panel === "english"} onClose={onClose} subject="english" year={year} cfg={cfg} onPlay={onPlay} onTopicTap={onTopicTap} />

      {/* TODAY'S ADVENTURE — preview card (Years 2–6) */}
      <Sheet open={panel === "adventure"} onClose={onClose} title="Today's Adventure" testid="panel-adventure">
        {adventure && (
          <div className="overflow-hidden rounded-[1.5rem] border border-border bg-gradient-to-br from-secondary/25 via-card to-teal/10 p-5">
            <div className="flex items-start gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-secondary text-white shadow-glow">
                <AdvIcon size={30} weight="fill" />
              </span>
              <span className="min-w-0">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Year {year} · {adventure.subject}</span>
                <h3 className="mt-0.5 font-display text-2xl font-black leading-tight text-foreground" data-testid="adventure-title">{adventure.title}</h3>
              </span>
            </div>
            <p className="mt-3 text-sm font-semibold text-foreground/80">{adventure.goal}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-teal/15 px-3 py-1.5 text-xs font-extrabold text-teal-ink">
              <Sparkle size={13} weight="fill" /> Growing — coming soon
            </span>
          </div>
        )}
        <p className="mt-3 text-center text-xs font-bold text-muted-foreground">A new adventure blooms at the signpost every day.</p>
      </Sheet>

      {/* HOMEWORK — the Postbox */}
      <Sheet open={panel === "homework"} onClose={onClose} title="The Postbox" testid="panel-homework">
        <div className="rounded-2xl border-2 border-dashed border-secondary bg-secondary/10 p-4">
          <p className="font-display text-base font-extrabold text-foreground">Dear {child.name},</p>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-muted-foreground">
            {cfg.band === "early" ? "No homework today! Go play, and watch your world grow." : "Nothing is due right now. When your teacher sets work, it will arrive here."}
          </p>
          <p className="mt-3 flex items-center gap-1.5 text-sm font-extrabold text-foreground">
            <Heart size={16} weight="fill" className="text-destructive" /> Your teacher
          </p>
        </div>
        <p className="mt-3 text-center text-xs font-bold text-muted-foreground">Letters from school will arrive here.</p>
      </Sheet>

      {/* MY PLACE — architected to grow: identity → achievements, favourites, creations, settings */}
      <Sheet open={panel === "my-place"} onClose={onClose} title="My Place" testid="panel-my-place">
        <div className="flex items-center gap-4">
          <ChildAvatar variant={child.avatar} className="h-16 w-16 drop-shadow-sm" />
          <span>
            <span className="block font-display text-2xl font-black text-foreground" data-testid="child-name">{child.name}</span>
            <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-teal/15 px-2.5 py-0.5 text-xs font-extrabold text-teal-ink">
              <Star size={12} weight="fill" /> Year {year} {STAGES[year]}
            </span>
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-muted/60 p-3 text-center">
            <FlowerIcon size={22} weight="fill" className="mx-auto text-primary" />
            <p className="mt-1 font-display text-2xl font-black text-foreground" data-testid="garden-count">{elementsCount}</p>
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Living things grown</p>
          </div>
          <div className="rounded-2xl bg-muted/60 p-3 text-center">
            <Star size={22} weight="fill" className="mx-auto text-secondary" />
            <p className="mt-1 font-display text-2xl font-black text-foreground">{elementsCount * 5}</p>
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Stars collected</p>
          </div>
        </div>
        <div className="mt-4">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
            <UserCircle size={14} weight="bold" /> Your place is growing
          </p>
          <div className="grid grid-cols-4 gap-2" data-testid="my-place-future">
            {FUTURE_SECTIONS.map(({ id, label, icon: Icon }) => (
              <div key={id} className="flex flex-col items-center gap-1 rounded-2xl border border-dashed border-border bg-background p-2.5 text-center opacity-70">
                <Icon size={18} weight="bold" className="text-muted-foreground" />
                <span className="text-[9px] font-extrabold leading-tight text-muted-foreground">{label}</span>
                <span className="rounded-full bg-muted px-1.5 text-[8px] font-black uppercase text-muted-foreground">Soon</span>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-center text-xs font-bold text-muted-foreground">Every adventure you finish grows something new in your world.</p>
      </Sheet>
    </>
  );
}
